import React, { useState } from 'react';
import { Card } from './ui/Card';
import { Sparkles, Send, MessageSquare, RotateCcw, Maximize2, UserCircle2, ArrowUpRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useBiltonAI } from '../contexts/BiltonAIContext';
interface PromptGroup {
  title: string;
  prompts: string[];
}
const PROMPT_GROUPS: PromptGroup[] = [{
  title: '🔥 Most Popular',
  prompts: ['Which are the 3 projects most likely to have a safety incident?', "Show me the worst safety analysis in today's preshift in my projects", 'Tell me about weird behaviour of Headcount in my projects']
}, {
  title: 'Safety & Risk',
  prompts: ['What are the top safety risks across all my sites this week?', 'Which contractors have the most safety violations this month?', 'Compare safety scores between my East Coast regions']
}, {
  title: 'Documents & Compliance',
  prompts: ['Which sites have the most expired documents right now?', 'Show me workers with expiring certifications in the next 7 days', "What's our overall document compliance rate?"]
}, {
  title: 'Inspections & Logs',
  prompts: ['Which sites missed inspections this week?', "Summarize today's inspection findings across all projects", 'Show me the trend of incomplete pre-shifts over the last month']
}, {
  title: 'Workers & Workforce',
  prompts: ['Which sites are understaffed compared to plan today?', 'Show me new workers who started this week across all sites', "What's the average time workers spend on site before their first incident?"]
}, {
  title: 'Trends & Analytics',
  prompts: ['How has our overall risk score changed over the past 4 weeks?', 'Which project type has the best safety record?', 'Give me a weekly safety summary I can share with leadership']
}];
export function BiltonAIWidget() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const {
    messages,
    isTyping,
    sendMessage,
    clearMessages
  } = useBiltonAI();
  const handleSend = (text: string) => {
    if (!text.trim()) return;
    sendMessage(text);
    setQuery('');
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(query);
    }
  };
  return <Card className="h-full flex flex-col relative overflow-hidden bg-gradient-to-br from-white to-indigo-50/30" noPadding data-id="element-39">
      {/* Header */}
      <div className="px-4 py-3 border-b border-indigo-100 flex items-center justify-between bg-white/50 backdrop-blur-sm" data-id="element-40">
        <div className="flex items-center gap-2" data-id="element-41">
          <div className="bg-indigo-600 p-1.5 rounded-lg" data-id="element-42">
            <Sparkles size={16} className="text-white" data-id="element-43" />
          </div>
          <h2 className="font-bold text-gray-900" data-id="element-44">BiltonAI</h2>
        </div>
        <div className="flex items-center gap-2" data-id="element-45">
          {messages.length > 0 && <button onClick={clearMessages} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 transition-colors px-2 py-1 rounded-md hover:bg-indigo-50" title="New chat" data-id="element-46">
              <RotateCcw size={13} data-id="element-47" />
              New chat
            </button>}
          <button onClick={() => navigate('/bilton-ai')} className="flex items-center justify-center text-gray-400 hover:text-indigo-600 transition-colors p-1.5 rounded-md hover:bg-indigo-50" title="Expand to full screen" data-id="element-48">
            <Maximize2 size={14} data-id="element-49" />
          </button>
          <span className="text-xs font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full border border-indigo-100" data-id="element-50">
            Beta
          </span>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-51">
        {messages.length === 0 ? <div className="flex flex-col text-center space-y-4" data-id="element-52">
            <div className="flex flex-col items-center pt-2 pb-1 flex-shrink-0" data-id="element-53">
              <div className="bg-indigo-50 p-3 rounded-full mb-3" data-id="element-54">
                <Sparkles size={24} className="text-indigo-600" data-id="element-55" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-56">
                Ask me anything about your projects
              </h3>
              <p className="text-xs text-gray-500 max-w-xs mx-auto" data-id="element-57">
                I can analyze safety trends, document compliance, and worker
                statistics across all your sites.
              </p>
            </div>

            <div className="w-full space-y-3 text-left pb-2" data-id="element-58">
              {PROMPT_GROUPS.map((group, groupIndex) => <div key={groupIndex} data-id="element-59">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5 px-1" data-id="element-60">
                    {group.title}
                  </p>
                  <div className="space-y-1.5" data-id="element-61">
                    {group.prompts.map((prompt, promptIndex) => <button key={promptIndex} onClick={() => handleSend(prompt)} className="w-full text-left px-3 py-2 rounded-lg border border-indigo-100 bg-white hover:bg-indigo-50 hover:border-indigo-200 transition-all duration-200 text-xs text-gray-700 flex items-start gap-2.5 group shadow-sm" data-id="element-62">
                        <MessageSquare size={13} className="text-indigo-400 mt-0.5 flex-shrink-0 group-hover:text-indigo-600 transition-colors" data-id="element-63" />
                        <span className="leading-relaxed" data-id="element-64">{prompt}</span>
                      </button>)}
                  </div>
                </div>)}
            </div>
          </div> : <>
            {messages.map((msg, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-65">
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-white border border-gray-100 shadow-sm text-gray-800 rounded-bl-none'}`} data-id="element-66">
                  {msg.text}
                </div>
              </motion.div>)}
            {isTyping && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex justify-start" data-id="element-67">
                <div className="bg-white border border-gray-100 shadow-sm p-3 rounded-2xl rounded-bl-none flex gap-1" data-id="element-68">
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-69" />
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-70" />
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-71" />
                </div>
              </motion.div>}
          </>}
      </div>

      {/* Input Area */}
      <div className="p-3 bg-white border-t border-gray-100 space-y-2" data-id="element-72">
        {/* Expand hint — shown once there are messages */}
        <AnimatePresence data-id="element-73">
          {messages.length > 0 && messages.length <= 4 && <motion.button initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} onClick={() => navigate('/bilton-ai')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 hover:border-indigo-200 transition-all group cursor-pointer" data-id="element-74">
              <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-75">
                <Maximize2 size={12} className="text-indigo-500" data-id="element-76" />
                <UserCircle2 size={12} className="text-indigo-500" data-id="element-77" />
              </div>
              <span className="text-[11px] text-indigo-700 leading-tight text-left" data-id="element-78">
                <span className="font-semibold" data-id="element-79">Expand for full view</span>
                <span className="text-indigo-500" data-id="element-80">
                  {' '}
                  · Role-based prompts for Executives, PMs, Supers & more
                </span>
              </span>
              <ArrowUpRight size={12} className="text-indigo-400 group-hover:text-indigo-600 transition-colors flex-shrink-0 ml-auto" data-id="element-81" />
            </motion.button>}
        </AnimatePresence>

        <div className="relative flex items-center" data-id="element-82">
          <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask a question..." className="w-full pl-4 pr-12 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all" data-id="element-83" />
          <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="absolute right-2 p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors" data-id="element-84">
            <Send size={14} data-id="element-85" />
          </button>
        </div>
      </div>
    </Card>;
}